import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { Currency, CurrencySymbol } from "_shared/money/currencies/currency";
import Money from "_shared/money/money";
import React from "react";
import { NumericFormat } from "react-number-format";

const CurrencyInputContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  height: "min-content"
}));
const AmountField = styled(NumericFormat)(() => ({
  '& .MuiInputBase-root': {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0
  }
})) as typeof NumericFormat;
const CurrencyField = styled(Select)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  '&.MuiInputBase-root': {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  }
}));

type CurrencyProps = {
  className?: string;
  size?: "small" | "medium";
  variant?: "standard" | "outlined" | "filled";
  value?: Money;
  label?: string;
  readOnly?: boolean;
  disabled?: boolean;
  allowNegative?: boolean;
  // `undefined` is the empty field, and it has to survive the trip out. Reporting 0 for a field
  // the user cleared hands the caller a figure nobody entered - and a caller that validates on an
  // absent rate would see one present and let it be saved.
  onChange: (currency: Currency, amount: number | undefined) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onBlurCapture?: React.FocusEventHandler<HTMLInputElement>;
}

export default function CurrencyInput(props: Readonly<CurrencyProps>) {
  const {
    className,
    size,
    variant,
    value, 
    label, 
    readOnly,
    disabled,
    allowNegative,
    onChange,
    onKeyDown,
    onBlurCapture
  } = props;

  const [currency, setCurrency] = React.useState(value?.currency ?? Currency.USD);
  // No value means no amount, not zero: a caller that has nothing to show gets an empty field
  // rather than a $0 that reads as a figure someone entered.
  const [amount, setAmount] = React.useState<number | undefined>(value?.amount);

  return (
    <CurrencyInputContainer className={className}>
      <AmountField
        allowNegative={allowNegative}
        readOnly={readOnly}
        disabled={disabled}
        prefix={CurrencySymbol(currency)}
        label={label}
        customInput={TextField}
        value={amount ?? ""}
        type="text"
        size={size ?? 'medium'}
        variant={variant ?? "outlined"}
        style={{ minWidth: '66px'}}
        thousandSeparator={true}
        decimalScale={2}
        onValueChange={(values, _sourceInfo) => {
          if (disabled || readOnly) return;
          setAmount(values.floatValue);
          onChange(currency as Currency, values.floatValue);
        }}
        onKeyDown={onKeyDown}
        onBlurCapture={onBlurCapture}
      />
      <CurrencyField
        value={currency}
        disabled={disabled}
        size={size ?? 'medium'}
        variant={variant ?? "outlined"}
        style={{ fontSize: '0.95em' }}
        onChange={(event) => {
          if (disabled || readOnly) return;
          setCurrency(
            Currency[event.target.value as keyof typeof Currency]
          );
          // Picking a currency for an empty field reports nothing: there is no amount yet, and
          // saying zero would hand the caller a figure the user never entered. The currency is
          // held here and goes out with the amount as soon as one is typed.
          if (amount === undefined) return;
          onChange(
            Currency[event.target.value as keyof typeof Currency],
            amount
          );
        }}
        onBlurCapture={onBlurCapture}
      >
        {Object.values(Currency).map((currencyType) => (
          <MenuItem key={currencyType} value={currencyType}>
            {currencyType} ({CurrencySymbol(currencyType)})
          </MenuItem>
        ))}
      </CurrencyField>
    </CurrencyInputContainer>
  );
}
